// src/components/PatternManagementModal.jsx
import React, { useState, useMemo } from "react";
import { Search, Edit3, Trash2, FolderPlus, Move } from "lucide-react";

export function PatternManagementModal({
  onClose,
  savedPatterns,
  onRenamePattern,
  onDeletePattern,
  deleteFolder,
}) {
  const [activeTab, setActiveTab] = useState("rename");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingFolder, setEditingFolder] = useState("");
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [selectedFolderForDeletion, setSelectedFolderForDeletion] = useState("");

  // Get unique folders
  const uniqueFolders = useMemo(() => {
    return Array.from(
      new Set(savedPatterns.map((p) => p.folder || "Uncategorized"))
    ).sort();
  }, [savedPatterns]);

  // Filter patterns based on search
  const filteredPatterns = useMemo(() => {
    if (!searchTerm) return savedPatterns;
    return savedPatterns.filter(
      (pattern) =>
        pattern.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pattern.folder || "Uncategorized")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [savedPatterns, searchTerm]);

  // Group patterns by folder
  const groupedPatterns = useMemo(() => {
    return filteredPatterns.reduce((groups, pattern) => {
      const folder = pattern.folder || "Uncategorized";
      if (!groups[folder]) groups[folder] = [];
      groups[folder].push(pattern);
      return groups;
    }, {});
  }, [filteredPatterns]);

  const handleStartEdit = (pattern) => {
    setEditingPatternId(pattern.id);
    setEditingName(pattern.name);
    setEditingFolder(pattern.folder || "");
  };

  const handleSaveEdit = async () => {
    if (editingPatternId && editingName.trim()) {
      await onRenamePattern(editingPatternId, editingName.trim(), editingFolder.trim());
      setEditingPatternId(null);
      setEditingName("");
      setEditingFolder("");
    }
  };

  const handleCancelEdit = () => {
    setEditingPatternId(null);
    setEditingName("");
    setEditingFolder("");
  };

  const handlePatternSelection = (patternId) => {
    const newSelected = new Set(selectedPatterns);
    if (newSelected.has(patternId)) {
      newSelected.delete(patternId);
    } else {
      newSelected.add(patternId);
    }
    setSelectedPatterns(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedPatterns.size === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedPatterns.size} selected pattern(s)?`
    );
    
    if (confirmed) {
      selectedPatterns.forEach((patternId) => {
        onDeletePattern(patternId);
      });
      setSelectedPatterns(new Set());
    }
  };

  const handleFolderDelete = () => {
    if (!selectedFolderForDeletion) return;
    
    const patternsInFolder = savedPatterns.filter(
      (p) => (p.folder || "Uncategorized") === selectedFolderForDeletion
    );
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the folder "${selectedFolderForDeletion}" and all ${patternsInFolder.length} pattern(s) inside it?`
    );
    
    if (confirmed) {
      deleteFolder(selectedFolderForDeletion);
      setSelectedFolderForDeletion("");
    }
  };

  const tabs = [
    { id: "rename", label: "Rename", icon: Edit3 },
    { id: "delete", label: "Delete", icon: Trash2 },
    { id: "organize", label: "Organize", icon: Move },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Manage Patterns</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium ${
                  activeTab === tab.id
                    ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Bar (shown for rename and organize tabs) */}
          {(activeTab === "rename" || activeTab === "organize") && (
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search patterns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Rename Tab */}
          {activeTab === "rename" && (
            <div className="space-y-4">
              {Object.keys(groupedPatterns).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? "No patterns match your search" : "No patterns found"}
                </div>
              ) : (
                Object.entries(groupedPatterns)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([folder, patterns]) => (
                    <div key={folder} className="border rounded-lg">
                      <div className="bg-gray-50 px-4 py-2 font-medium text-sm border-b">
                        {folder}
                      </div>
                      <div className="p-2">
                        {patterns
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((pattern) => (
                            <div
                              key={pattern.id}
                              className="flex items-center justify-between p-3 hover:bg-gray-50 rounded"
                            >
                              {editingPatternId === pattern.id ? (
                                <div className="flex-1 flex gap-2">
                                  <input
                                    type="text"
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    className="flex-1 px-2 py-1 border rounded"
                                    placeholder="Pattern name"
                                  />
                                  <input
                                    type="text"
                                    value={editingFolder}
                                    onChange={(e) => setEditingFolder(e.target.value)}
                                    className="w-32 px-2 py-1 border rounded"
                                    placeholder="Folder"
                                  />
                                  <button
                                    onClick={handleSaveEdit}
                                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <span className="flex-1">{pattern.name}</span>
                                  <button
                                    onClick={() => handleStartEdit(pattern)}
                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}

          {/* Delete Tab */}
          {activeTab === "delete" && (
            <div className="space-y-6">
              {/* Delete Individual Patterns */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium text-lg mb-3 flex items-center">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Patterns
                </h3>
                
                {selectedPatterns.size > 0 && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-red-700">
                        {selectedPatterns.size} pattern(s) selected
                      </span>
                      <div className="space-x-2">
                        <button
                          onClick={() => setSelectedPatterns(new Set())}
                          className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Clear Selection
                        </button>
                        <button
                          onClick={handleBulkDelete}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete Selected
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {Object.entries(groupedPatterns)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([folder, patterns]) => (
                      <div key={folder}>
                        <div className="font-medium text-sm text-gray-600 mb-1">
                          {folder}
                        </div>
                        {patterns
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((pattern) => (
                            <label
                              key={pattern.id}
                              className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPatterns.has(pattern.id)}
                                onChange={() => handlePatternSelection(pattern.id)}
                                className="mr-3"
                              />
                              <span className="flex-1">{pattern.name}</span>
                            </label>
                          ))}
                      </div>
                    ))}
                </div>
              </div>

              {/* Delete Folders */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium text-lg mb-3 flex items-center">
                  <FolderPlus className="w-5 h-5 mr-2" />
                  Delete Entire Folders
                </h3>
                
                <div className="flex gap-2">
                  <select
                    value={selectedFolderForDeletion}
                    onChange={(e) => setSelectedFolderForDeletion(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="">Select a folder to delete...</option>
                    {uniqueFolders.map((folder) => {
                      const patternCount = savedPatterns.filter(
                        (p) => (p.folder || "Uncategorized") === folder
                      ).length;
                      return (
                        <option key={folder} value={folder}>
                          {folder} ({patternCount} pattern{patternCount !== 1 ? 's' : ''})
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={handleFolderDelete}
                    disabled={!selectedFolderForDeletion}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Delete Folder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Organize Tab */}
          {activeTab === "organize" && (
            <div className="space-y-4">
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                <Move className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-lg font-medium mb-1">Coming Soon!</p>
                <p className="text-sm">
                  Drag-and-drop pattern organization will be available in a future update.
                </p>
                <p className="text-sm mt-2">
                  For now, you can use the Rename tab to move patterns between folders.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatternManagementModal;
